import React from 'react'
import {
  Bullet,
  Bullets,
  Info,
  Page,
  Section,
} from '../../components/DocsComponents'
import DocLink from '../../components/DocLink'

const GovernanceActionsDoc = () => (
  <Page
    title="Governance Actions"
    intro="The governance actions register is the single place where follow-up work raised across the governance module - audits, incidents, meetings, risks, complaints, KPI reviews, and the CQC Centre - is tracked through to completion."
  >
    <Section title="The register">
      <Bullets>
        <Bullet>
          Open <DocLink url="/governance-actions" text="Governance Actions" />{' '}
          for every action, with summary counts for All, Open, Overdue,
          Completed, and Cancelled. An action counts as overdue when it is
          still open past its due date.
        </Bullet>
        <Bullet>
          Each action carries a title, description, source, assignee, due
          date, and a priority (Low, Medium, High, or Critical).
        </Bullet>
        <Bullet>
          Filter the list by status and by source, and add a{' '}
          <strong>Manual</strong> action directly for work that didn't come
          from another record.
        </Bullet>
      </Bullets>
      <Info title="Who can do this?">
        Viewing and managing governance actions requires the Governance team
        (site administrators always have access). The register appears in the
        sidebar on tier 2.
      </Info>
    </Section>

    <Section title="Where actions come from">
      <Bullets>
        <Bullet>
          <strong>Audit</strong> - answers flagged "Action Required" when
          completing a governance audit (see{' '}
          <DocLink
            docsPage
            url="/clinical-governance/governance-audits"
            text="Governance Audits"
          />
          ).
        </Bullet>
        <Bullet>
          <strong>Incident</strong>, <strong>Meeting</strong>,{' '}
          <strong>Risk Register</strong> (see{' '}
          <DocLink
            docsPage
            url="/clinical-governance/risk-register"
            text="Risk Register"
          />
          ), <strong>Complaint</strong>, and <strong>KPI Review</strong> -
          follow-up work raised from those records.
        </Bullet>
        <Bullet>
          <strong>CQC Centre</strong> - an action raised against a CQC gap or
          a specific evidence item (for example "renew this DBS check") from{' '}
          <DocLink
            docsPage
            url="/clinical-governance/cqc-evidence-gaps-actions"
            text="Evidence, Gaps, and Assignments"
          />
          .
        </Bullet>
        <Bullet>
          <strong>Manual</strong> - added directly on the register.
        </Bullet>
      </Bullets>
    </Section>

    <Section title="Working an action">
      <Bullets>
        <Bullet>
          Open actions move between <strong>Open</strong> and{' '}
          <strong>In Progress</strong>, and can have their title, description,
          assignee, due date, and priority updated.
        </Bullet>
        <Bullet>
          <strong>Completing</strong> an action always requires completion
          notes describing what was done, and can attach evidence files. CQC
          actions can additionally record a resolution note - what was done to
          close the gap.
        </Bullet>
        <Bullet>
          <strong>Cancelling</strong> an action requires a reason, recorded in
          its place.
        </Bullet>
        <Bullet>
          A completed or cancelled action cannot be edited - it can only be
          reopened, which clears its completion record and returns it to Open.
        </Bullet>
      </Bullets>
    </Section>

    <Section title="Actions are not CQC gaps">
      <p>
        The CQC Centre's gaps and governance actions sound similar but behave
        differently. A gap is a computed finding: it cannot be dismissed, and
        it resolves itself automatically once the underlying issue is fixed. A
        governance action is a managed work item: it has an owner, a due date,
        and a status you move yourself, and closing it requires notes. Raising
        an action from a gap gives the fix an owner and a deadline - but the
        gap only clears when the fix actually lands.
      </p>
    </Section>
  </Page>
)

export default GovernanceActionsDoc
